import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { clearAuthClientState, consumeForceLogout, setCookie } from "utils/cookie";
import callApi, { resetSessionExpiredFlag } from "helpers/callApi";
import { getCookieDomainFromHostname } from "utils/environment";
import { fetchUser, fetchApp, switchApp } from "../user/userSlice";
import { parseServerError } from "utils/errors";

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password, code }, { rejectWithValue }) => {
    try {
      const body = code ? { email, password, code } : { email, password };
      const response = await callApi("/user/login", "POST", body);
      
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      
      const json = await response.json();

      if (json.require_two_factor === true) {
        return { require_two_factor: true };
      }
      

      if (json.detail || json.error) {
        throw new Error(json.detail || json.error);
      }
      
      storeUserData(json);
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/logout", "POST");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return true;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    } finally {
      clearAuthClientState();
    }
  }
);

export const activateUser = createAsyncThunk(
  "auth/activateUser",
  async (data, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/activate", "POST", data);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json();
      storeUserData(json);
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const checkAuthenticationCode = createAsyncThunk(
  "auth/checkAuthenticationCode",
  async ({ code, api_key }, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/mobile/check_authentication_code?authentication_code=${code}`,
        "GET",
        { "api-key": api_key }
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json();
      storeUserData(json);
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const twoFactorSetup = createAsyncThunk(
  "auth/twoFactorSetup",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/setup/", "POST");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json();
      return {
        secret: json.secret || null,
        provisioningUri: json.provisioning_uri || null,
      };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const twoFactorConfirm = createAsyncThunk(
  "auth/twoFactorConfirm",
  async ({ code }, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/confirm/", "POST", {
        code: String(code || "").trim(),
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      return json || { ok: true };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const disable2faSendEmailCode = createAsyncThunk(
  "auth/disable2faSendEmailCode",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/disable/send_email_code/", "POST");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      return json || { ok: true };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const verifyCode = createAsyncThunk(
  "auth/verifyCode",
  async ({ code, code_type, purpose }, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/verify_code/", "POST", {
        code: String(code || "").trim(),
        code_type,
        purpose,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      if (json.valid !== true) {
        return rejectWithValue("Invalid verification code. Try again.");
      }
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const verifyDisable2faCode = createAsyncThunk(
  "auth/verifyDisable2faCode",
  async ({ code, code_type }, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/verify_code/", "POST", {
        code: String(code || "").trim(),
        code_type,
        purpose: "disable_2fa",
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      if (json.valid !== true) {
        return rejectWithValue("Invalid verification code. Try again.");
      }
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const disable2fa = createAsyncThunk(
  "auth/disable2fa",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/two_factor/disable/", "POST");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      return json || { ok: true };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const sendPasswordResetEmailCode = createAsyncThunk(
  "auth/sendPasswordResetEmailCode",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/password_reset/send/", "POST");
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      return json || { ok: true };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const setNewPassword = createAsyncThunk(
  "auth/setNewPassword",
  async ({ new_password }, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/set_password", "POST", {
        new_password: String(new_password || "").trim(),
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const json = await response.json().catch(() => ({}));
      return json || { ok: true };
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

export const loginWithTwoFactor = createAsyncThunk(
  "auth/loginWithTwoFactor",
  async ({ code }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { email, password } = state.auth.pendingLogin || {};
      
      if (!email || !password) {
        return rejectWithValue("Missing credentials. Please login again.");
      }

      const response = await callApi("/user/login", "POST", {
        email,
        password,
        code: String(code || "").trim(),
      });

      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }

      const json = await response.json();
      
      if (json.detail || json.error) {
        return rejectWithValue(json.detail || json.error);
      }

      storeUserData(json);
      return json;
    } catch (err) {
      return rejectWithValue(err?.message || "Error");
    }
  }
);

// function storeUserData(json) {
//   localStorage.setItem("api_key", json.api_key);
//   localStorage.setItem("is_manager", json.is_manager || false);
//   // console.log('json json', json)
//
//   const now = new Date();
//   const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
//
//   setCookie("api_key", json.api_key, {
//     expires: nextWeek,
//     path: "/",
//     domain: ".dev.lappa.org",
//     Secure: true,
//     SameSite: "None"
//   });
//   setCookie("is_manager", json.is_manager || false, {
//     expires: nextWeek,
//     path: "/",
//     domain: ".dev.lappa.org",
//     Secure: true,
//     SameSite: "None"
//   });
// }

/** Get api_key from backend response (support different response shapes) */
function getApiKeyFromResponse(json) {
  if (!json) return null;
  const key =
    json.api_key ??
    json.token ??
    json.key ??
    json.access_token ??
    json.data?.api_key ??
    json.data?.token ??
    json.user?.api_key ??
    null;
  return key != null && key !== "" ? String(key) : null;
}

function storeUserData(json) {
  const apiKey = getApiKeyFromResponse(json);
    if (!apiKey || typeof apiKey !== "string") {
    if (process.env.NODE_ENV === "development") {
      console.warn("storeUserData: no api_key in response", json);
    }
    return;
  }

  consumeForceLogout();
  localStorage.setItem("api_key", apiKey);
  localStorage.setItem("is_manager", json.is_manager || false);

  const now = new Date();
  const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "[::1]" ||
    /\.local$/.test(window.location.hostname);

  // Use current host's domain for cookies (login.dev.lappa.org → .dev.lappa.org)
  const cookieDomain = getCookieDomainFromHostname() || process.env.REACT_APP_COOKIES || null;

  if (isLocalhost) {
    setCookie("api_key", apiKey, { expires: nextWeek, path: "/" });
    setCookie("is_manager", json.is_manager || false, {
      expires: nextWeek,
      path: "/",
    });
  } else if (cookieDomain) {
    setCookie("api_key", apiKey, {
      expires: nextWeek,
      path: "/",
      domain: cookieDomain,
      Secure: true,
      SameSite: "None",
    });
    setCookie("is_manager", json.is_manager || false, {
      expires: nextWeek,
      path: "/",
      domain: cookieDomain,
      Secure: true,
      SameSite: "None",
    });
  } else {
    setCookie("api_key", apiKey, {
      expires: nextWeek,
      path: "/",
      Secure: true,
      SameSite: "None",
    });
    setCookie("is_manager", json.is_manager || false, {
      expires: nextWeek,
      path: "/",
      Secure: true,
      SameSite: "None",
    });
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState: {
    isLogged: false,
    loggingIn: false,
    loggingInLoading: false,
    loggingOut: false,
    activating: false,
    checkAuthenticationCodeLoading: false,
    activationSuccessful: false,
    loginError: null,
    logoutError: null,
    activationError: null,
    checkAuthenticationCodeError: null,
    twoFactorSetup: {
      secret: null,
      provisioningUri: null,
    },
    twoFactorSetupLoading: false,
    twoFactorSetupError: null,
    twoFactorConfirmLoading: false,
    twoFactorConfirmError: null,
    disable2fa: {
      sendEmailLoading: false,
      sendEmailError: null,
      verifyLoading: false,
      verifyError: null,
      disableLoading: false,
      disableError: null,
      verified: false,
      verifiedBy: null,
      resendAvailableAt: null,
    },
    passwordReset: {
      sendEmailLoading: false,
      sendEmailError: null,
      verifyEmailLoading: false,
      verifyEmailError: null,
      verifyAuthLoading: false,
      verifyAuthError: null,
      setPasswordLoading: false,
      setPasswordError: null,
      resendAvailableAt: null,
      step: "idle",
    },
    pendingLogin: null,
    requireTwoFactor: false,
    twoFactorLoginLoading: false,
    twoFactorLoginError: null,
    app_id: 0
  },
  reducers: {
    startLogin(state) {
      state.isLogged = true;
      state.loggingIn = true;
    },
    resetDisable2faFlow(state) {
      state.disable2fa = {
        sendEmailLoading: false,
        sendEmailError: null,
        verifyLoading: false,
        verifyError: null,
        disableLoading: false,
        disableError: null,
        verified: false,
        verifiedBy: null,
        resendAvailableAt: null,
      };
    },
    clearDisable2faErrors(state) {
      state.disable2fa.sendEmailError = null;
      state.disable2fa.verifyError = null;
      state.disable2fa.disableError = null;
    },
    resetPasswordResetFlow(state) {
      state.passwordReset = {
        sendEmailLoading: false,
        sendEmailError: null,
        verifyEmailLoading: false,
        verifyEmailError: null,
        verifyAuthLoading: false,
        verifyAuthError: null,
        setPasswordLoading: false,
        setPasswordError: null,
        resendAvailableAt: null,
        step: "idle",
      };
    },
    clearPasswordResetErrors(state) {
      state.passwordReset.sendEmailError = null;
      state.passwordReset.verifyEmailError = null;
      state.passwordReset.verifyAuthError = null;
      state.passwordReset.setPasswordError = null;
    },
    resetTwoFactorLogin(state) {
      state.pendingLogin = null;
      state.requireTwoFactor = false;
      state.twoFactorLoginLoading = false;
      state.twoFactorLoginError = null;
    },
    clearTwoFactorLoginError(state) {
      state.twoFactorLoginError = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.isLogged = true;
        state.loggingIn = false;
        state.app_id = action.payload.app_id || 0;
        // Reset session expired flag after successful authentication
        resetSessionExpiredFlag();
      })
      .addCase(fetchApp.fulfilled, (state, action) => {
        // Convert to number to ensure consistent type
        state.app_id = action.payload ? Number(action.payload) : 0;
      })
      .addCase(switchApp.fulfilled, (state, action) => {
        // Convert to number to ensure consistent type
        state.app_id = action.payload ? Number(action.payload) : 0;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.isLogged = false;
        state.loggingIn = true;
      })
      .addCase(login.pending, (state) => {
        state.loggingIn = true;
        state.loggingInLoading = true;
        state.loginError = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loggingIn = false;
        state.loggingInLoading = false;
        state.loginError = action.payload;
      })
      .addCase(logout.pending, (state) => {
        state.loggingOut = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLogged = false;
        state.loggingOut = false;
        state.logoutError = null;
        state.app_id = 0;
        // Reset session expired flag on logout
        resetSessionExpiredFlag();
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLogged = false;
        state.loggingOut = false;
        state.logoutError = action.payload;
        resetSessionExpiredFlag();
      })
      .addCase(activateUser.pending, (state) => {
        state.activating = true;
        state.activationError = null;
        state.activationSuccessful = false;
      })
      .addCase(activateUser.fulfilled, (state, action) => {
        state.activating = false;
        state.activationError = null;
        state.activationSuccessful = true;
        state.isLogged = true;
        state.loggingIn = false;
        state.loginError = null;
        if (action.payload != null && typeof action.payload === "object" && action.payload.app_id != null) {
          state.app_id = Number(action.payload.app_id);
        }
      })
      .addCase(activateUser.rejected, (state, action) => {
        state.activating = false;
        state.activationError = action.payload;
        state.activationSuccessful = false;
      })
      .addCase(checkAuthenticationCode.pending, (state) => {
        state.checkAuthenticationCodeLoading = true;
      })
      .addCase(checkAuthenticationCode.fulfilled, (state) => {
        state.checkAuthenticationCodeError = null;
        state.checkAuthenticationCodeLoading = false;
      })
      .addCase(checkAuthenticationCode.rejected, (state, action) => {
        state.checkAuthenticationCodeLoading = false;
        state.checkAuthenticationCodeError = action.payload;
      })
      .addCase(twoFactorSetup.pending, (state) => {
        state.twoFactorSetupLoading = true;
        state.twoFactorSetupError = null;
      })
      .addCase(twoFactorSetup.fulfilled, (state, action) => {
        state.twoFactorSetupLoading = false;
        state.twoFactorSetupError = null;
        state.twoFactorSetup.secret = action.payload.secret;
        state.twoFactorSetup.provisioningUri = action.payload.provisioningUri;
      })
      .addCase(twoFactorSetup.rejected, (state, action) => {
        state.twoFactorSetupLoading = false;
        state.twoFactorSetupError = action.payload;
        state.twoFactorSetup.secret = null;
        state.twoFactorSetup.provisioningUri = null;
      })
      .addCase(twoFactorConfirm.pending, (state) => {
        state.twoFactorConfirmLoading = true;
        state.twoFactorConfirmError = null;
      })
      .addCase(twoFactorConfirm.fulfilled, (state) => {
        state.twoFactorConfirmLoading = false;
        state.twoFactorConfirmError = null;
      })
      .addCase(twoFactorConfirm.rejected, (state, action) => {
        state.twoFactorConfirmLoading = false;
        state.twoFactorConfirmError = action.payload;
      })
      .addCase(disable2faSendEmailCode.pending, (state) => {
        state.disable2fa.sendEmailLoading = true;
        state.disable2fa.sendEmailError = null;
      })
      .addCase(disable2faSendEmailCode.fulfilled, (state) => {
        state.disable2fa.sendEmailLoading = false;
        state.disable2fa.sendEmailError = null;
        state.disable2fa.resendAvailableAt = Date.now() + 60000;
      })
      .addCase(disable2faSendEmailCode.rejected, (state, action) => {
        state.disable2fa.sendEmailLoading = false;
        state.disable2fa.sendEmailError = parseServerError(action.payload ?? action.error);
      })
      .addCase(verifyDisable2faCode.pending, (state) => {
        state.disable2fa.verifyLoading = true;
        state.disable2fa.verifyError = null;
      })
      .addCase(verifyDisable2faCode.fulfilled, (state, action) => {
        state.disable2fa.verifyLoading = false;
        state.disable2fa.verifyError = null;
        state.disable2fa.verified = true;
        const codeType = action.meta.arg?.code_type;
        state.disable2fa.verifiedBy = codeType === "email" ? "email" : codeType === "authenticator" ? "authenticator" : null;
      })
      .addCase(verifyDisable2faCode.rejected, (state, action) => {
        state.disable2fa.verifyLoading = false;
        state.disable2fa.verifyError = parseServerError(action.payload ?? action.error);
        state.disable2fa.verified = false;
        state.disable2fa.verifiedBy = null;
      })
      .addCase(disable2fa.pending, (state) => {
        state.disable2fa.disableLoading = true;
        state.disable2fa.disableError = null;
      })
      .addCase(disable2fa.fulfilled, (state) => {
        state.disable2fa.disableLoading = false;
        state.disable2fa.disableError = null;
      })
      .addCase(disable2fa.rejected, (state, action) => {
        state.disable2fa.disableLoading = false;
        state.disable2fa.disableError = parseServerError(action.payload ?? action.error);
      })
      .addCase(sendPasswordResetEmailCode.pending, (state) => {
        state.passwordReset.sendEmailLoading = true;
        state.passwordReset.sendEmailError = null;
      })
      .addCase(sendPasswordResetEmailCode.fulfilled, (state) => {
        state.passwordReset.sendEmailLoading = false;
        state.passwordReset.sendEmailError = null;
        state.passwordReset.resendAvailableAt = Date.now() + 60000;
        state.passwordReset.step = "email";
      })
      .addCase(sendPasswordResetEmailCode.rejected, (state, action) => {
        state.passwordReset.sendEmailLoading = false;
        state.passwordReset.sendEmailError = parseServerError(action.payload ?? action.error);
      })
      .addCase(verifyCode.pending, (state, action) => {
        const purpose = action.meta.arg?.purpose;
        const codeType = action.meta.arg?.code_type;
        
        if (purpose === "password_reset") {
          if (codeType === "email") {
            state.passwordReset.verifyEmailLoading = true;
            state.passwordReset.verifyEmailError = null;
          } else if (codeType === "authenticator") {
            state.passwordReset.verifyAuthLoading = true;
            state.passwordReset.verifyAuthError = null;
          }
        }
      })
      .addCase(verifyCode.fulfilled, (state, action) => {
        const purpose = action.meta.arg?.purpose;
        const codeType = action.meta.arg?.code_type;
        
        if (purpose === "password_reset") {
          if (codeType === "email") {
            state.passwordReset.verifyEmailLoading = false;
            state.passwordReset.verifyEmailError = null;
            if (action.payload.is_two_factor_enabled === true) {
              state.passwordReset.step = "authenticator";
            } else {
              state.passwordReset.step = "newPassword";
            }
          } else if (codeType === "authenticator") {
            state.passwordReset.verifyAuthLoading = false;
            state.passwordReset.verifyAuthError = null;
            state.passwordReset.step = "newPassword";
          }
        }
      })
      .addCase(verifyCode.rejected, (state, action) => {
        const purpose = action.meta.arg?.purpose;
        const codeType = action.meta.arg?.code_type;
        
        if (purpose === "password_reset") {
          if (codeType === "email") {
            state.passwordReset.verifyEmailLoading = false;
            state.passwordReset.verifyEmailError = parseServerError(action.payload ?? action.error);
          } else if (codeType === "authenticator") {
            state.passwordReset.verifyAuthLoading = false;
            state.passwordReset.verifyAuthError = parseServerError(action.payload ?? action.error);
          }
        }
      })
      .addCase(setNewPassword.pending, (state) => {
        state.passwordReset.setPasswordLoading = true;
        state.passwordReset.setPasswordError = null;
      })
      .addCase(setNewPassword.fulfilled, (state) => {
        state.passwordReset.setPasswordLoading = false;
        state.passwordReset.setPasswordError = null;
        state.passwordReset.step = "done";
      })
      .addCase(setNewPassword.rejected, (state, action) => {
        state.passwordReset.setPasswordLoading = false;
        state.passwordReset.setPasswordError = parseServerError(action.payload ?? action.error);
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload && action.payload.require_two_factor === true) {
          state.loggingIn = false;
          state.loggingInLoading = false;
          state.loginError = null;
          return;
        }
        
        state.isLogged = true;
        state.loggingIn = false;
        state.loggingInLoading = false;
        state.loginError = null;
        state.app_id = action.payload.app_id;
        resetSessionExpiredFlag();
      })
      .addCase(loginWithTwoFactor.pending, (state) => {
        state.twoFactorLoginLoading = true;
        state.twoFactorLoginError = null;
      })
      .addCase(loginWithTwoFactor.fulfilled, (state, action) => {
        state.twoFactorLoginLoading = false;
        state.twoFactorLoginError = null;
        state.pendingLogin = null;
        state.requireTwoFactor = false;
        state.isLogged = true;
        state.loggingIn = false;
        state.loggingInLoading = false;
        state.loginError = null;
        state.app_id = action.payload.app_id;
        resetSessionExpiredFlag();
      })
      .addCase(loginWithTwoFactor.rejected, (state, action) => {
        state.twoFactorLoginLoading = false;
        state.twoFactorLoginError = parseServerError(action.payload ?? action.error);
      });
  },
});

export const { startLogin, resetDisable2faFlow, clearDisable2faErrors, resetPasswordResetFlow, clearPasswordResetErrors, resetTwoFactorLogin, clearTwoFactorLoginError } = authSlice.actions;

export default authSlice.reducer;
